import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, FileSignature, Binoculars, Mail, Sparkles, UserSquare2, QrCode, MessageSquareQuote, FileBarChart } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { SiteBreadcrumbs } from "@/components/marketing/SiteBreadcrumbs";

export type AiIndexDict = {
  breadcrumb: string;
  badge: string;
  h1a: string;
  h1b: string;
  intro: string;
  tryIt: string;
  comingSoon: string;
  moreA: string;
  moreB: string;
  live: { to: string; name: string; desc: string }[];
  soon: { name: string; desc: string }[];
};

export const Route = createFileRoute("/ai/")({
  head: () => ({
    meta: [
      { title: "Free AI Tools for Sales, Outreach & Contracts | banana.cards" },
      { name: "description", content: "Free AI tools for founders, agencies and sales teams. Generate contracts, find leads, write cold emails, build business bios and more — all in seconds." },
      { property: "og:title", content: "Free AI Tools — banana.cards" },
      { property: "og:description", content: "Free AI generators for contracts, leads, cold emails and more." },
      { property: "og:url", content: "https://banana.cards/ai" },
    ],
    links: [{ rel: "canonical", href: "https://banana.cards/ai" }],
  }),
  component: Page,
});

const LIVE_ICONS = [FileSignature, Binoculars, Mail];
const SOON_ICONS = [UserSquare2, QrCode, MessageSquareQuote, FileBarChart];

export const DICT_EN: AiIndexDict = {
  breadcrumb: "AI Tools",
  badge: "Free AI tools by Banana",
  h1a: "Free AI tools for the work",
  h1b: "that pays the bills.",
  intro: "Draft contracts, find leads, write outreach — in seconds, in the browser, no credit card. Built on the same engine as the full Banana platform.",
  tryIt: "Try it free",
  comingSoon: "Coming soon",
  moreA: "More free tools,",
  moreB: "on the way.",
  live: [
    { to: "/ai/contract-generator", name: "AI Contract Generator", desc: "Draft NDAs, freelance agreements, service contracts and more — ready to e-sign." },
    { to: "/ai/lead-finder", name: "AI Lead Finder", desc: "Find qualified B2B leads and local prospects on Google Maps in plain English." },
    { to: "/ai/email-writer", name: "AI Email Writer", desc: "Cold outreach, follow-ups and welcome emails — on-tone, on-brand, ready to send." },
  ],
  soon: [
    { name: "AI Bio Generator", desc: "Generate a punchy professional bio for your digital business card." },
    { name: "AI QR Code Builder", desc: "Create on-brand QR codes that route to whatever you want — and track scans." },
    { name: "AI Review Reply", desc: "Reply to every Google review with on-brand answers in seconds." },
    { name: "AI Proposal Generator", desc: "Turn a project brief into a beautiful, branded proposal." },
  ],
};

export function Page({ dict = DICT_EN }: { dict?: AiIndexDict } = {}) {
  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div aria-hidden className="ai-bg-gradient pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-12 md:px-6 md:pt-16">
          <SiteBreadcrumbs className="mb-6" currentName={dict.breadcrumb} />
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-card/70 px-3 py-1 text-xs font-semibold backdrop-blur ring-1 ring-foreground/10">
              <Sparkles className="h-3.5 w-3.5" />
              {dict.badge}
            </span>
            <h1 className="mt-5 font-display text-4xl leading-[1.05] md:text-6xl">
              {dict.h1a} <em className="italic">{dict.h1b}</em>
            </h1>
            <p className="mt-5 text-base text-muted-foreground md:text-lg">
              {dict.intro}
            </p>
          </div>

          {/* Live tools */}
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {dict.live.map((t, i) => {
              const Icon = LIVE_ICONS[i] ?? Sparkles;
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  className="group rounded-2xl bg-card p-6 transition hover:bg-muted"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[linear-gradient(135deg,#6366f1_0%,#a855f7_50%,#ec4899_100%)] text-white shadow-[0_6px_16px_-4px_rgba(168,85,247,0.55)]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-lg font-bold">{t.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold">
                    {dict.tryIt} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coming soon */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{dict.comingSoon}</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              {dict.moreA} <em className="italic">{dict.moreB}</em>
            </h2>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {dict.soon.map((t, i) => {
              const Icon = SOON_ICONS[i] ?? Sparkles;
              return (
                <div key={t.name} className="rounded-2xl bg-card p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-base font-bold">{t.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}